import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, Heart, Users, Calendar, Sparkles, Facebook, Youtube } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { api } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1639254241852-1e9062106f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwY2h1cmNoJTIwd29yc2hpcCUyMGNvbW11bml0eXxlbnwwfHx8fDE3NzY1MzkzNjR8MA&ixlib=rb-4.1.0&q=85";
const ARCH_IMG = "https://customer-assets.emergentagent.com/job_church-connect-165/artifacts/26kkp99v_1b1ae41e-d3dc-44c6-b6f6-b12b5633bf07.png";

export default function Home() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="ETI worship" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-olive/90 via-olive/70 to-olive/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-40 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-bone animate-fade-up">
            <p className="overline text-terracotta mb-6">El-Shaddai Tabernacle International</p>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-balance">
              Occupy <span className="text-terracotta italic font-light">&</span><br />Build.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-bone/85 max-w-2xl leading-relaxed">
              A house of worship, family, and mission in Nelspruit — believing God to
              transform lives, advance the Kingdom, and raise a generation that builds.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    data-testid="hero-vision-video-btn"
                    className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none px-8 h-14 text-base font-semibold group"
                  >
                    <Play size={18} className="mr-2 group-hover:translate-x-0.5 transition-transform" fill="currentColor" />
                    Watch Our Vision
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl p-0 bg-black border-none">
                  <div className="aspect-video">
                    <iframe
                      src="https://www.youtube.com/embed/videoseries?list=UUelshaddaitabernacle"
                      title="ETI Vision"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                      allowFullScreen
                    />
                  </div>
                </DialogContent>
              </Dialog>
              <Link to="/membership">
                <Button
                  variant="outline"
                  className="border-2 border-bone text-bone bg-transparent hover:bg-bone hover:text-olive rounded-none px-8 h-14 text-base font-semibold"
                  data-testid="hero-join-btn"
                >
                  Become a Member
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="bg-bone/95 backdrop-blur p-8 border-l-4 border-terracotta">
              <p className="overline text-olive mb-4">Service Times</p>
              <div className="space-y-5">
                <div>
                  <p className="font-heading font-bold text-2xl text-ink">Sunday</p>
                  <p className="text-muted_ink">Intercession (Prayer) · 08:00</p>
                  <p className="text-muted_ink">Main Service · 08:30 – 10:30</p>
                </div>
                <div className="h-px bg-border" />
                <div>
                  <p className="font-heading font-bold text-2xl text-ink">Wednesday</p>
                  <p className="text-muted_ink">Homecell · 18:30</p>
                </div>
                <div className="h-px bg-border" />
                <div>
                  <p className="font-heading font-bold text-2xl text-ink">Friday</p>
                  <p className="text-muted_ink">Youth Gathering · 17:00</p>
                  <p className="text-muted_ink">Prayer Night · 19:00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-olive text-bone border-y border-olive-hover overflow-hidden" aria-hidden="true">
        <div className="flex whitespace-nowrap animate-marquee py-5 font-heading text-2xl sm:text-3xl font-black tracking-tight">
          {[...Array(6)].map((_, i) => (
            <span key={i} className="flex items-center gap-8 px-8">
              <span>OCCUPY</span>
              <span className="text-terracotta">✦</span>
              <span>BUILD</span>
              <span className="text-terracotta">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* VISION / ABOUT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div
            role="img"
            aria-label="ETI community"
            className="w-full aspect-[4/5] bg-olive"
            style={{
              backgroundImage: `url(${ARCH_IMG})`,
              backgroundSize: "250%",
              backgroundPosition: "50% 5%",
              backgroundRepeat: "no-repeat",
            }}
          />
        </div>
        <div className="lg:col-span-7 lg:pl-8">
          <p className="overline text-terracotta mb-6">Our Story · Since 1994</p>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-olive leading-[1.05]">
            Founded in <em className="text-terracotta not-italic">1994</em> by Bishop VP Mdaka.
          </h2>
          <p className="mt-8 text-lg text-ink/80 leading-relaxed max-w-2xl">
            El-Shaddai Tabernacle International is a move of God with a clear mission — to
            <span className="font-semibold text-olive"> occupy and build</span>. Birthed out of a
            divine calling, ETI is dedicated to uplifting lives and leading people back to God.
            What began as a seed in Mpumalanga has grown into a family of churches across
            South Africa — raising sons and daughters to impact every sphere of society.
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Sparkles, title: "Mission", text: "Reaching people through evangelism and building them through discipleship." },
              { icon: Heart, title: "Vision", text: "Called to occupy all communities and build the kingdom of God." },
              { icon: Users, title: "Branches", text: "21 in the spread of Mpumalanga and Gauteng." },
            ].map((b) => (
              <div key={b.title} className="border-l-2 border-terracotta pl-5">
                <b.icon className="text-olive mb-3" size={22} />
                <h4 className="font-heading font-bold text-lg text-ink">{b.title}</h4>
                <p className="text-sm text-muted_ink mt-1">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="bg-cream py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="overline text-terracotta mb-3">Get Involved</p>
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-olive">
                Plug in. Build with us.
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/people/Elshaddai-Tabernacle-International-Nelspruit/100068296793353/"
                target="_blank" rel="noreferrer"
                className="w-12 h-12 bg-white border border-border hover:bg-olive hover:text-bone hover:border-olive flex items-center justify-center transition-colors"
                data-testid="quick-social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.youtube.com/@ETIMBOMBELA"
                target="_blank" rel="noreferrer"
                className="w-12 h-12 bg-white border border-border hover:bg-olive hover:text-bone hover:border-olive flex items-center justify-center transition-colors"
                data-testid="quick-social-youtube"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: "/giving", label: "Give", desc: "Fund the mission. Sow a seed.", icon: Heart, accent: true },
              { to: "/membership", label: "Membership", desc: "Find your home here.", icon: Users },
              { to: "/bible-school", label: "Bible School", desc: "Learn. Grow. Go.", icon: Sparkles },
              { to: "/locations", label: "Find a Church", desc: "6 branches across Mpumalanga.", icon: Calendar },
            ].map((card) => (
              <Link
                key={card.to}
                to={card.to}
                data-testid={`quick-link-${card.label.toLowerCase().replace(" ", "-")}`}
                className={`group p-8 border transition-all hover:-translate-y-1 ${
                  card.accent
                    ? "bg-terracotta border-terracotta text-white hover:bg-terracotta-hover"
                    : "bg-white border-border hover:border-olive hover:shadow-lg"
                }`}
              >
                <card.icon size={26} className={card.accent ? "text-white" : "text-olive"} />
                <h3 className="font-heading font-bold text-2xl mt-6">{card.label}</h3>
                <p className={`mt-2 text-sm ${card.accent ? "text-white/85" : "text-muted_ink"}`}>
                  {card.desc}
                </p>
                <div className="mt-8 flex items-center gap-2 text-sm font-semibold">
                  Explore <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="overline text-terracotta mb-3">What's Next</p>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-olive">Upcoming Events</h2>
          </div>
          <Link to="/events" className="text-olive font-semibold hover:text-terracotta flex items-center gap-2">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((e) => (
            <Link
              to="/events"
              key={e.id}
              className="bg-white border border-border overflow-hidden group hover:shadow-lg hover:-translate-y-1 transition-all block"
              data-testid={`home-event-${e.id}`}
            >
              {e.image_url && (
                <div className="aspect-[4/3] overflow-hidden bg-olive flex items-center justify-center">
                  <img src={e.image_url} alt={e.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
              <div className="p-6">
                <p className="text-xs font-bold text-terracotta tracking-widest uppercase">
                  {new Date(e.date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <h3 className="font-heading font-bold text-xl text-ink mt-2 group-hover:text-olive transition-colors">{e.title}</h3>
                <p className="text-sm text-muted_ink mt-2 line-clamp-2">{e.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* GIVING CTA */}
      <section className="bg-olive text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-terracotta mb-4">Partner With Us</p>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
              Your generosity fuels the mission.
            </h2>
            <p className="mt-6 text-bone/80 max-w-lg leading-relaxed">
              Give through PayFast or Yoco. Every seed plants something eternal — lives changed,
              buildings raised, souls saved.
            </p>
          </div>
          <div className="md:text-right">
            <Link to="/giving">
              <Button className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none px-10 h-14 text-base font-semibold" data-testid="home-give-cta">
                Give Now <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
